/**
 * StatusDot — minimal colored dot for inline status (task list rows,
 * candidate names, etc.). When you only need the dot, not the label.
 *
 *   <StatusDot domain="task" status="in_progress" />
 *   <StatusDot domain="application" status="rejected" size="md" />
 *
 * Resolution path mirrors StatusBadge: (domain, status) →
 * `DOMAIN_STATUS_INTENT` → `Intent` → `INTENT_CLASSES.dot`.
 */

import * as React from "react";
import { cn } from "../utils";
import {
  DOMAIN_STATUS_INTENT,
  INTENT_CLASSES,
  type Intent,
  type StatusDomain,
  type StatusOf,
} from "../tokens";

const SIZE: Record<"xs" | "sm" | "md", string> = {
  xs: "size-1.5",
  sm: "size-2",
  md: "size-2.5",
};

export function StatusDot<D extends StatusDomain>({
  domain,
  status,
  size = "sm",
  className,
  title,
}: {
  domain: D;
  status: StatusOf<D>;
  size?: keyof typeof SIZE;
  className?: string;
  title?: string;
}) {
  const intent =
    (DOMAIN_STATUS_INTENT[domain] as Record<string, Intent>)[status as string] ??
    "neutral";
  return (
    <span
      aria-hidden={!title}
      role={title ? "img" : undefined}
      title={title}
      className={cn(
        "inline-block rounded-full shrink-0",
        SIZE[size],
        INTENT_CLASSES[intent].dot,
        className,
      )}
    />
  );
}

/**
 * IntentDot — direct-intent variant for cases without a domain enum.
 */
export function IntentDot({
  intent,
  size = "sm",
  className,
  title,
}: {
  intent: Intent;
  size?: keyof typeof SIZE;
  className?: string;
  title?: string;
}) {
  return (
    <span
      aria-hidden={!title}
      role={title ? "img" : undefined}
      title={title}
      className={cn(
        "inline-block rounded-full shrink-0",
        SIZE[size],
        INTENT_CLASSES[intent].dot,
        className,
      )}
    />
  );
}
